import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTrade } from "@/contexts/TradeContext";
import { TradePicker } from "@/components/trades/TradePicker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export function TradesSettingsPanel() {
  const { orgId } = useAuth();
  const { orgTrades, refetch, loading: tradesLoading } = useTrade();
  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected(orgTrades.map((t) => t.trade_code));
  }, [orgTrades]);

  const save = async () => {
    if (!orgId) return;
    if (selected.length === 0) {
      toast.error("Select at least one trade");
      return;
    }
    setSaving(true);
    try {
      const current = orgTrades.map((t) => t.trade_code);
      const toAdd = selected.filter((c) => !current.includes(c));
      const toRemove = current.filter((c) => !selected.includes(c));

      if (toAdd.length > 0) {
        const { error } = await supabase
          .from("org_trades")
          .insert(toAdd.map((trade_code) => ({ org_id: orgId, trade_code, is_active: true })));
        if (error) throw error;
      }
      if (toRemove.length > 0) {
        const { error } = await supabase
          .from("org_trades")
          .delete()
          .eq("org_id", orgId)
          .in("trade_code", toRemove);
        if (error) throw error;
      }

      await refetch();
      toast.success("Trades updated");
    } catch (err: any) {
      console.error(err);
      toast.error(err.message ?? "Failed to update trades");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Your Trades</CardTitle>
        <CardDescription>
          Pick every trade your business does. Each trade gets its own workflows,
          terminology, and tools — switch between them from the top bar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {tradesLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <TradePicker value={selected} onChange={setSelected} disabled={saving} />
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save trades
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
